import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartConstants } from "@modules/apc/apc/data/chart-modules";
import { ChartMappingData } from "@modules/apc/apc/data/chart-mapping-data";
import { CustomRendererService } from "@shared/services/custom-renderer.service";
import {
  PerformanceDevelopmentChartPreviewService
} from "@shared/services/performance-development-chart-preview/performance-development-chart-preview.service";
import { HelperService } from "@shared/services/helper.service";
import { X_AXIS_TYPE } from "@shared/enums/x-axis-type.enum";

@Component({
  selector: 'app-performance-development-chart-preview',
  templateUrl: './performance-development-chart-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceDevelopmentChartPreviewComponent implements OnInit, OnChanges {
  private customRendererService: CustomRendererService = inject(CustomRendererService);
  private pdChartPreviewService: PerformanceDevelopmentChartPreviewService = inject(PerformanceDevelopmentChartPreviewService);
  private helperService: HelperService = inject(HelperService);

  public uniqueChartPostfixId: string;
  public chartIdWithoutHash: string;

  @Input() public config: any;
  @Input() public chartType: string;

  public ngOnInit(): void {
    this.uniqueChartPostfixId = ChartConstants.PERFORMANCE_DEVELOPMENT.ELEMENT_ID+'_'+ this.helperService.generateRandomString();
    this.chartIdWithoutHash = this.uniqueChartPostfixId.substring(1);
    this.generateChart(this.config, this.uniqueChartPostfixId);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.config.previousValue) {
      this.generateChart(changes.config.currentValue, this.uniqueChartPostfixId);
    }
  }

  /**
   * Preprocesses the plot data to ensure x-axis dates are properly aligned
   * and no gaps appear between data points on the x-axis
   * @param plot The original plot configuration
   * @returns A new plot configuration with adjusted x-axis settings
   */
  private preprocessPlotData(plot: any): any {
    // Create a deep copy of the plot to avoid modifying the original
    const processedPlot = this.helperService.makeCopy(plot);

    // Only process if we have lines and we're dealing with datetime x-axis
    if (processedPlot &&
      processedPlot.lines &&
      processedPlot.lines.length > 0 &&
      processedPlot.x_axis_type === 'datetime') {

      // Extract all x-axis values (dates) from all lines
      const allXValues: number[] = [];
      const dateStrings: string[] = [];

      // Create mappings for each line to store original dates
      processedPlot.date_mapping = {};

      processedPlot.lines.forEach((line: any, lineIndex: number) => {
        if (line.source && line.source.x && Array.isArray(line.source.x)) {
          // Store original dates
          const originalDates: any = [];

          // Convert all dates to timestamps
          const timestamps = line.source.x.map((date: any, i: number) => {
            const timestamp = typeof date === 'string' || date instanceof Date ?
              new Date(date).getTime() : date;

            // Format date as string
            const dateObj = new Date(timestamp);
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            const dateStr = `${day}.${month}.${year}`;

            originalDates.push(dateStr);

            if (!dateStrings.includes(dateStr)) {
              dateStrings.push(dateStr);
            }

            return timestamp;
          });

          // Store original dates for this line
          processedPlot.date_mapping[lineIndex] = originalDates;

          allXValues.push(...timestamps);
        }
      });

      // Sort unique x values (timestamps) and date strings
      const uniqueXValues = [...new Set(allXValues)].sort((a, b) => a - b);
      dateStrings.sort((a, b) => {
        const dateA = new Date(a.split('.').reverse().join('-'));
        const dateB = new Date(b.split('.').reverse().join('-'));
        return dateA.getTime() - dateB.getTime();
      });

      // If we have at least 2 data points, adjust plot for even spacing
      if (uniqueXValues.length >= 2) {
        // Create timestamp to index mapping
        const dateToIndexMap: any = {};
        uniqueXValues.forEach((timestamp, index) => {
          dateToIndexMap[timestamp] = index;
        });

        // Store x-axis labels
        processedPlot.x_labels = dateStrings;

        // Process each line to use indices instead of timestamps
        processedPlot.lines.forEach((line: any, lineIndex: number) => {
          if (line.source && line.source.x && Array.isArray(line.source.x)) {
            // Get correct date strings for this line
            const lineDateStrings = processedPlot.date_mapping[lineIndex] || [];

            // Create formatted date array for tooltips
            line.source.date_display = lineDateStrings;

            // Convert x values to indices
            line.source.x = line.source.x.map((date: any) => {
              const timestamp = typeof date === 'string' || date instanceof Date ?
                new Date(date).getTime() : date;
              return dateToIndexMap[timestamp] !== undefined ? dateToIndexMap[timestamp] : 0;
            });
          }
        });

        // Set up custom ticks and ranges
        processedPlot.custom_x_ticks = Object.values(dateToIndexMap);
        processedPlot.use_custom_x_axis = true;
        processedPlot.x_labels_sorted = dateStrings;
      }
    }

    return processedPlot;
  }

  public generateChart(plot: any, elementId: string): void {
    const payload = {
      plot,
      testId: 10,
      elementId: elementId,
      type: ChartConstants.PERFORMANCE_DEVELOPMENT.ID,
      test_type: 'newtype',
      legend: 'legends',
      mappingData: ChartMappingData,
    };

    setTimeout(() => {
      this.customRendererService.empty(elementId);

      if (this.chartType === X_AXIS_TYPE.DATETIME) {
        this.pdChartPreviewService.generatePerformanceDevelopmentChart(payload.plot, payload.elementId, payload.testId);
      } else if (this.chartType === X_AXIS_TYPE.DATE_GAP) {
        const processedPlot = this.preprocessPlotData(payload.plot);

        this.pdChartPreviewService.generatePerformanceDevelopmentChart(processedPlot, payload.elementId, payload.testId);
      }
    });
  }
}
